import React from 'react';
import styled from '@benzinga/themetron';
import { Icon } from '@benzinga/core-ui';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { sanitizeHTML } from '@benzinga/frontend-utils';

type VideoCardVariant = 'default' | 'show';

export interface VideoCardProps {
  className?: string;
  id: string;
  title: string;
  featuredVideoTitle?: string;
  featuredVideoId?: string;
  playlistId?: string;
  duration?: number | null;
  variant?: VideoCardVariant;
  showHref?: string;
  showName?: string;
}

export const VideoCard: React.FC<VideoCardProps> = ({
  duration,
  featuredVideoTitle,
  id,
  playlistId,
  title,
  variant = 'default',
}) => {
  const prettyTimeFormat = (duration: number) => {
    const hrs = Math.floor(duration / 3600);
    const mins = Math.floor((duration % 3600) / 60);
    const secs = Math.floor(duration % 60);

    let formattedTime = '';

    if (hrs > 0) {
      formattedTime += `${hrs}:${mins < 10 ? '0' : ''}`;
    }

    formattedTime += `${mins}:${secs < 10 ? '0' : ''}`;
    formattedTime += `${secs}`;
    return formattedTime;
  };

  return (
    <VideoCardContainer className={`video-card ${variant}`}>
      <a href={`https://www.youtube.com/watch?v=${id}`} rel="noopener noreferrer" target="_blank">
        {variant === 'show' && (
          <div className="title-header" dangerouslySetInnerHTML={{ __html: sanitizeHTML(title) }} />
        )}
        <div className="content">
          <img alt={title} src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`} />
          {variant === 'default' && duration && (
            <span
              className="timestamp"
              style={{
                backdropFilter: 'blur(8px)',
                backgroundImage: 'linear-gradient(180deg, rgba(45, 84, 139, 0.75) 0%, rgba(46, 71, 105, 0.75) 94.77%)',
              }}
            >
              {prettyTimeFormat(duration)}
            </span>
          )}
          {variant === 'default' && (
            <div className="play-icon-wrapper">
              <Icon className="text-white" icon={faPlay} />
            </div>
          )}
          {variant === 'show' && (
            <div className="overlay">
              <span className="title">{featuredVideoTitle}</span>
              <div className="play-icon-wrapper">
                <Icon className="text-white" icon={faPlay} />
              </div>
            </div>
          )}
        </div>
      </a>
      {variant === 'default' && (
        <div className="title">
          <a href={`https://www.youtube.com/watch?v=${id}`} rel="noopener noreferrer" target="_blank">
            <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(title) }} />
          </a>
        </div>
      )}
      {variant === 'show' && (
        <div className="view-all-footer">
          <a href={`https://www.youtube.com/playlist?list=${playlistId}`} rel="noopener noreferrer" target="_blank">
            View All <Icon className="right-chevron-icon" icon={faChevronRight} />
          </a>
        </div>
      )}
    </VideoCardContainer>
  );
};

const VideoCardContainer = styled.div`
  &.video-card {
    overflow: hidden;
    border-radius: ${({ theme }) => theme.borderRadius.default};

    > a {
      position: relative;

      .content {
        position: relative;
        padding-bottom: 56.2%;
        overflow: hidden;

        img {
          object-fit: cover;
          width: 100%;
          height: 100%;
          position: absolute;
          max-width: 100%;
          transform: scale(1.03);
        }

        .timestamp {
          backdrop-filter: blur(8px);
          background-image: linear-gradient(rgba(45, 84, 139, 0.75) 0%, rgba(46, 71, 105, 0.75) 94.77%);
          color: ${({ theme }) => theme.colorPalette.white};
          font-weight: 600;
          font-size: 1rem;
          line-height: 1.5rem;
          text-align: center;
          padding-top: 0.25rem;
          padding-bottom: 0.25rem;
          padding-left: 0.75rem;
          padding-right: 0.75rem;
          border-radius: 0.25rem;
          box-sizing: border-box;
          bottom: 0.5rem;
          right: 0.5rem;
          position: absolute;
        }

        .play-icon-wrapper {
          box-shadow: 0px 4px 28px #1979ff;
          background-color: ${({ theme }) => theme.colorPalette.blue600};
          border-radius: 9999px;
          display: flex;
          justify-content: center;
          align-items: center;
          transform: translateX(-50%) translateY(-50%) rotate(0) skewX(0) skewY(0) scaleX(1) scaleY(1);
          width: 3rem;
          height: 3rem;
          left: 50%;
          top: 50%;
          position: absolute;

          > span {
            color: ${({ theme }) => theme.colorPalette.white};
            font-size: ${({ theme }) => theme.fontSize.xs};
          }
        }
      }
    }

    .title {
      color: ${({ theme }) => theme.colorPalette.gray900};
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
      margin-top: 0.35rem;
      text-overflow: ellipsis;
      overflow: hidden;

      a {
        color: ${({ theme }) => theme.colorPalette.gray900};
      }

      [data-emoji] {
        font-style: normal;
        font-weight: normal;
      }
    }

    &.show {
      .title-header {
        display: flex;
        align-items: center;
        height: 50px;
        color: ${({ theme }) => theme.colorPalette.gray600};
        background-color: ${({ theme }) => theme.colorPalette.white};
        border: 1px solid ${({ theme }) => theme.colors.border};
        border-bottom: none;
        font-weight: ${({ theme }) => theme.fontWeight.semibold};
        text-transform: uppercase;
        padding: 0 15px;
      }

      .content {
        .title {
          position: absolute;
          bottom: 20px;
          left: 20px;
          color: ${({ theme }) => theme.colorPalette.white};
          width: calc(100% - 200px);
          font-weight: ${({ theme }) => theme.fontWeight.medium};

          [data-emoji] {
            font-style: normal;
            font-weight: normal;
          }
        }

        .play-icon-wrapper {
          right: -5px;
          bottom: -5px;
          left: initial;
          top: initial;
          box-shadow: rgb(25 121 255) 0px 0px 40px;
        }
      }

      .view-all-footer {
        width: 100%;

        a {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 0.6rem 1.5rem;
          text-transform: uppercase;
          font-weight: ${({ theme }) => theme.fontWeight.semibold};
          background-color: #f2f8ff;
          color: ${({ theme }) => theme.colorPalette.gray600};
          border: 1px solid ${({ theme }) => theme.colors.border};
          overflow: hidden;
        }

        .right-chevron-icon {
          color: ${({ theme }) => theme.colorPalette.blue600};
        }
      }

      .overlay {
        backdrop-filter: blur(1.5px);
        -webkit-backdrop-filter: blur(1.5px);
        mix-blend-mode: normal;
        width: 100%;
        height: 120px;
        position: absolute;
        bottom: 0;
        box-shadow: rgb(14 40 62 / 72%) inset 0 -80px 20px 10px;
      }
    }
  }
`;
